/**
 * /operations/transactions/contract-amendment/new — Contract
 * Amendment HR-initiate form (M30.5.4).
 *
 * Prospective updates only — the AI assist panels surface
 * salary delta + Article 53/60 + EOS-base impact warnings.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmployeePicker } from "../../components/ui/EmployeePicker";
import { TransactionFormShell } from "../../components/hr_operations";
import { api, type EmployeePickerOption } from "../../lib/api";
import { useHrActor } from "../../lib/use-hr-actor";
import { useTransactionAssist } from "../../lib/use-transaction-assist";

export function ContractAmendmentHrInitiatePage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<EmployeePickerOption | null>(
    null,
  );
  const [reasonCode, setReasonCode] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [effectiveDate, setEffectiveDate] = useState<string>("");
  const [proposedSalary, setProposedSalary] = useState<string>("");
  const [proposedProbationDays, setProposedProbationDays] = useState<
    string
  >("");
  const [consentDecrease, setConsentDecrease] = useState<boolean>(false);
  const [consentProbation, setConsentProbation] = useState<boolean>(
    false,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reasonCodes = useQuery({
    queryKey: ["reason-codes", "contract_amendment"],
    queryFn: () => api.listReasonCodes("contract_amendment"),
    staleTime: 5 * 60_000,
  });

  // Actor identity from Clerk (audit fix C1 — commandment 39).
  const actor = useHrActor();

  const enabled =
    actor.isReady && !!employee && !!reasonCode && !!effectiveDate;
  const body = {
    actor_user_id: actor.actorUserId,
    actor_role: actor.actorRole,
    target_employee_id: employee?.id ?? "",
    reason_code: reasonCode,
    reason_explanation: explanation || null,
    effective_date: effectiveDate,
    proposed_basic_salary: proposedSalary || null,
    proposed_probation_period_days: proposedProbationDays
      ? Number(proposedProbationDays)
      : null,
    employee_consent_to_decrease: consentDecrease,
    employee_consent_to_probation_extension: consentProbation,
  };
  const { result, isLoading, error, acknowledgedCodes, acknowledge } =
    useTransactionAssist({
      body,
      enabled,
      callAssist: (b) => api.contractAmendmentHrInitiateAssist(b),
    });

  async function submit() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.contractAmendmentHrInitiateCommit({
        ...body,
        ai_findings_acknowledged: Array.from(acknowledgedCodes),
      });
      setSubmitSuccess(true);
      setTimeout(() => navigate("/operations/hub"), 1500);
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <TransactionFormShell
      transactionType="contract_amendment"
      title={t("operations.contract_amendment_form_title")}
      assist={result}
      isAssistLoading={isLoading}
      assistError={error}
      acknowledgedCodes={acknowledgedCodes}
      onAcknowledge={acknowledge}
      onAcceptSuggestion={(field, value) => {
        if (field === "reason_explanation" && typeof value === "string") {
          setExplanation(value);
        }
      }}
      onSubmit={submit}
      isSubmitting={isSubmitting || !actor.isReady}
      submitSuccess={submitSuccess}
      submitError={submitError}
    >
      <Field label={t("operations.form_employee_label")}>
        <EmployeePicker
          value={employee?.id ?? null}
          onChange={setEmployee}
          selected={employee}
        />
      </Field>
      <Field label={t("operations.form_reason_code_label")}>
        <select
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          <option value="">—</option>
          {reasonCodes.data?.codes.map((c) => (
            <option key={c.code} value={c.code}>
              {isAr ? c.display_ar : c.display_en}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("operations.contract_amendment_form_effective_date")}>
        <input
          type="date"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={t("operations.contract_amendment_form_basic_salary")}
        >
          <input
            type="number"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={proposedSalary}
            onChange={(e) => setProposedSalary(e.target.value)}
            min={0}
            step="0.01"
            dir="ltr"
          />
        </Field>
        <Field
          label={t("operations.contract_amendment_form_probation_days")}
        >
          <input
            type="number"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={proposedProbationDays}
            onChange={(e) => setProposedProbationDays(e.target.value)}
            min={0}
            max={180}
            dir="ltr"
          />
        </Field>
      </div>
      <Field label={t("operations.form_reason_explanation_label")}>
        <textarea
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          rows={3}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </Field>
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={consentDecrease}
          onChange={(e) => setConsentDecrease(e.target.checked)}
        />
        {t("operations.contract_amendment_form_consent_decrease")}
      </label>
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={consentProbation}
          onChange={(e) => setConsentProbation(e.target.checked)}
        />
        {t("operations.contract_amendment_form_consent_probation")}
      </label>
    </TransactionFormShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
